/**
 * Hook عام لقراءة بانرات مكان معيّن مع فلترة الجمهور + استهداف سلوكيّ.
 *
 * تريغرات السلّة:
 *  - cart_returning_visitor: السلّة فيها منتجات + الجلسة الحاليّة جديدة
 *    (sessionStorage لا يحوي علامة `lecmo_session_seen` → دخول جديد بعد إغلاق التبويب).
 *  - from_abandoned_cart_link: URL يحوي `?reminder=cart` (رابط حملة سلّة مهجورة).
 */
import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { useLocale } from "@/contexts/LocaleContext";

import { useWishlist } from "@/hooks/useWishlist";
import { useCartStore } from "@/stores/cartStore";
import { toBannerMarket, type BannerPlacement, type BannerMarket, type BannerRow } from "@/utils/banner.types";


export interface BannersFilterCtx {
  isAmbassador?: boolean;
  ambassadorTier?: "decouverte" | "active" | "elite" | null;
  completedOrders?: number;
}

const SESSION_FLAG = "lecmo_session_seen";
// All placements in this document share the same arrival decision. Reading and
// consuming the flag per hook lets the top strip hide return campaigns elsewhere.
let returningInThisDocument: boolean | undefined;

function isNewBannerSession(): boolean {
  if (typeof window === "undefined") return false;
  if (returningInThisDocument !== undefined) return returningInThisDocument;
  try {
    returningInThisDocument = !window.sessionStorage.getItem(SESSION_FLAG);
    if (returningInThisDocument) window.sessionStorage.setItem(SESSION_FLAG, "1");
  } catch {
    returningInThisDocument = false;
  }
  return returningInThisDocument;
}

function audienceMatches(banner: BannerRow, signedIn: boolean, ctx: BannersFilterCtx): boolean {
  switch (banner.audience) {
    case "all":
      return true;
    case "anonymous":
      return !signedIn;
    case "authenticated":
      return signedIn;
    case "lover":
      return signedIn && (!ctx.isAmbassador || ctx.ambassadorTier === "decouverte");
    case "ambassador":
      return !!ctx.isAmbassador && ctx.ambassadorTier === "active";
    case "super_star":
      return !!ctx.isAmbassador && ctx.ambassadorTier === "elite";
    default:
      return true;
  }
}

function triggerMatches(
  banner: BannerRow,
  wishlistHandles: Set<string>,
  cartHasItems: boolean,
  isReturningVisitor: boolean,
  fromAbandonedCartLink: boolean,
): boolean {
  if (!banner.trigger_type || banner.trigger_type === "none") return true;
  if (banner.trigger_type === "wishlist_contains") {
    if (!banner.trigger_value) return true;
    return wishlistHandles.has(banner.trigger_value);
  }
  if (banner.trigger_type === "cart_returning_visitor") {
    return cartHasItems && isReturningVisitor;
  }
  if (banner.trigger_type === "from_abandoned_cart_link") {
    return fromAbandonedCartLink;
  }
  if (banner.trigger_type === "cart_or_wishlist") {
    return cartHasItems || wishlistHandles.size > 0;
  }
  return true;
}

const EMPTY_CTX: BannersFilterCtx = {};

async function fetchActiveBannersForPlacement(
  placement: BannerPlacement,
  market: BannerMarket,
): Promise<{ items: BannerRow[] }> {
  const params = new URLSearchParams({ placement, market });
  const response = await fetch(`/api/banners?${params.toString()}`, {
    method: "GET",
    headers: { Accept: "application/json" },
  });
  if (!response.ok) return { items: [] };
  return response.json() as Promise<{ items: BannerRow[] }>;
}

export function useBannersByPlacement(placement: BannerPlacement, ctx: BannersFilterCtx = EMPTY_CTX) {
  const { user, loading } = useAuth();
  const { effectiveMarket } = useLocale();
  const { items: wishlistItems } = useWishlist();
  const cartItems = useCartStore((s) => s.items);

  // علامة "الزائر عاد بعد إغلاق التبويب": sessionStorage فارغة في أوّل تحميل ثمّ نملؤها.
  const [isReturningVisitor, setIsReturningVisitor] = useState(false);
  useEffect(() => {
    setIsReturningVisitor(isNewBannerSession());
  }, []);

  // فحص URL مرّة واحدة لرابط حملة السلّة المهجورة.
  const [fromAbandonedCartLink, setFromAbandonedCartLink] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("reminder") === "cart") setFromAbandonedCartLink(true);
  }, []);

  // الأسواق المدعومة في المتجر تشمل ~68 دولة مضافة ليست أسواق بانرات،
  // ومدقّق /api/banners يقبل مجموعة البانرات فقط، لذا تُحوَّل ما خارجها إلى
  // ROW — نفس السلة التي كان يزورها هؤلاء الزوّار قبل الإضافة.
  const bannerMarket = toBannerMarket(effectiveMarket);


  const q = useQuery({
    queryKey: ["banners", placement, bannerMarket],
    queryFn: () => fetchActiveBannersForPlacement(placement, bannerMarket),
    staleTime: 60_000,
  });


  const wishlistHandles = useMemo(() => {
    const s = new Set<string>();
    for (const it of wishlistItems) if (it.product_handle) s.add(it.product_handle);
    return s;
  }, [wishlistItems]);

  const cartHasItems = cartItems.length > 0;

  // أوّل صورة من السلّة (للبانرات المربوطة بتريغر السلّة).
  const firstCartImage = useMemo<string | null>(() => {
    for (const it of cartItems) {
      const url = it?.product?.node?.images?.edges?.[0]?.node?.url;
      if (url) return url;
    }
    return null;
  }, [cartItems]);

  // صورة من الـwishlist: لو trigger_value مطابق لِـ handle نستخدم صورته،
  // وإلّا نستخدم أوّل صورة في القائمة.
  const wishlistImageFor = (handle: string | null): string | null => {
    if (handle) {
      const exact = wishlistItems.find((w) => w.product_handle === handle);
      if (exact?.product_image) return exact.product_image;
    }
    for (const w of wishlistItems) if (w.product_image) return w.product_image;
    return null;
  };

  const items = useMemo(() => {
    const rows = q.data?.items ?? [];
    if (loading) return [] as BannerRow[];
    return rows
      .filter((b) => {
        if (!audienceMatches(b, !!user, ctx)) return false;
        if (b.min_completed_orders > 0 && (ctx.completedOrders ?? 0) < b.min_completed_orders) {
          return false;
        }
        if (!triggerMatches(b, wishlistHandles, cartHasItems, isReturningVisitor, fromAbandonedCartLink)) {
          return false;
        }
        return true;
      })
      .map((b): BannerRow => {
        // عند تريغرات السلّة/الأمنيات: استخدم صورة المنتج بدلاً من الصورة المرفوعة يدويّاً.
        if (b.trigger_type === "cart_returning_visitor" || b.trigger_type === "from_abandoned_cart_link") {
          return { ...b, image_url: firstCartImage ?? b.image_url };
        }
        if (b.trigger_type === "wishlist_contains") {
          const img = wishlistImageFor(b.trigger_value);
          return { ...b, image_url: img ?? b.image_url };
        }
        if (b.trigger_type === "cart_or_wishlist") {
          // أولويّة: صورة أوّل منتج في السلّة، فإن لم توجد فصورة أوّل منتج في قائمة الأمنيات.
          const img = firstCartImage ?? wishlistImageFor(null);
          return { ...b, image_url: img ?? b.image_url };
        }
        return b;
      });
  }, [q.data, user, loading, ctx, wishlistHandles, cartHasItems, isReturningVisitor, fromAbandonedCartLink, firstCartImage, wishlistItems]);

  return { items, isLoading: q.isLoading };
}
